import { FC, useState, MouseEvent } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  dataStorageKeywords,
  discountDialogTitles,
  discountDialogSubtitles,
  errorMessages,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { Discount } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { discountInitialValues } from 'src/utils/initialValues.utils';
import { discountValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const DiscountActionMenuButton: FC<{
  rowId: any;
  name: string;
  status: string;
  resetPageAndRefresh: () => void;
  fetchDiscounts: () => void;
  deleteDiscount: (rowId: string, companyId: string) => void;
}> = ({ rowId, name, resetPageAndRefresh, fetchDiscounts, deleteDiscount }) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleEditFormSubmit = async () => {
    if (!selectedDiscount) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_DISCOUNT, {
        id: rowId,
        name: formik.values.name,
        description: formik.values.description,
        percentage:
          formik.values.percentage === '' ? null : formik.values.percentage,
        defaultValue:
          formik.values.defaultValue === '' ? null : formik.values.defaultValue,
      });
      setEditDialogOpen(false);
      fetchDiscounts();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (discount: Discount) => {
    setSelectedDiscount(discount);

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_DISCOUNT_BY_ID,
        {
          params: { id: discount.id },
        }
      );

      formik.setValues({
        name: discount.name,
        description: response.data.description,
        percentage: response.data.percentage,
        defaultValue: response.data.defaultValue,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    try {
      setEditDialogOpen(false);
      setSelectedDiscount(null);
      formik.resetForm();
    } catch (error) {
      showSnackbar(errorMessages.ERROR_CLOSING_DIALOG, 'error');
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setSelectedName(name);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedName) {
      return;
    }
    const companyId = Cookies.get(dataStorageKeywords.COMPANY_ID);

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId && companyId) {
          deleteDiscount(rowId, companyId);
        }
        resetPageAndRefresh();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedName(null);
    }
  };

  const formik = useFormik({
    initialValues: discountInitialValues,
    validationSchema: discountValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const getDialogHeight = () =>
    calculateDialogHeight(formik, [
      ['name'],
      ['description'],
      ['percentage'],
      ['defaultValue'],
    ]) + 80;

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                name: name,
                description: '',
                percentage: 0,
                defaultValue: 0,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={discountDialogTitles.EDIT}
            subtitle={discountDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedDiscount && (
              <>
                <TextField
                  width="480px"
                  id="name"
                  label="Discount Name"
                  formikProps={formik}
                />

                <TextArea
                  id="description"
                  label="Description"
                  formikProps={formik}
                />

                <TextField
                  width="480px"
                  id="percentage"
                  label="Percentage"
                  formikProps={formik}
                />
                <TextField
                  width="480px"
                  id="defaultValue"
                  label="Default Value"
                  formikProps={formik}
                />
              </>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={discountDialogTitles.DELETE}
        subtitle={discountDialogSubtitles.DELETE}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={'var(--error)'}
        alertIconColor={'var(--error)'}
      />
    </>
  );
};
export default DiscountActionMenuButton;
